import { useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { Button } from '@/components/ui/button';
import { AuditLog } from '@/shared/types/auditLogs';
import { timeFromNowShort } from '@/util/dateUtils';
import { hasAuditLogDiff } from '@/util/diffUtils';

import { type GroupedLog } from './activityLogGrouping';
import { type ActivityLogLookups } from './useActivityLogLookups';
import TileActivityLog, { AuditLogFieldChanges } from '../TileActivityLog';

const MINIMUM_GROUPED_ACTIONS = 2;

// Elbow (└) into the collapsed toggle; one SVG path so the stroke stays uniform.
const BranchConnector = () => (
  <svg
    width="12"
    height="19"
    viewBox="0 0 12 19"
    fill="none"
    aria-hidden
    className="shrink-0 self-start text-muted-foreground/50"
  >
    <path
      d="M0.5 0V14.5C0.5 16.7091 2.29086 18.5 4.5 18.5H12"
      stroke="currentColor"
      strokeLinecap="round"
    />
  </svg>
);

interface Props {
  group: GroupedLog;
  /** Shared id→value resolvers, built once by the feed (see useActivityLogLookups). */
  lookups: ActivityLogLookups;
  hideAgentLink?: boolean;
}

// Agent events often lack target_name; resolve it from the agent id so the row never shows a UUID.
export const resolveTargetName = (
  activity: AuditLog,
  getBrain: (id: string | null) => string | undefined
): string | null =>
  activity.target_name ??
  (activity.target_type === 'brains' ? (getBrain(activity.target_id) ?? null) : null);

/**
 * One feed entry: a timestamp gutter on the left and the activity sentence on
 * the right. When the group holds ≥2 activities it renders a single collapsed
 * header row plus a "View N activities" toggle that expands the nested rows.
 */
const ActivityGroupRow = ({ group, lookups, hideAgentLink = false }: Props) => {
  const { t } = useTranslation();
  // `nested` is empty for a single activity and holds ≥2 entries for a real group.
  const isGrouped = group.nested.length >= MINIMUM_GROUPED_ACTIONS;

  const [expanded, setExpanded] = useState(false);
  const { getSubject, getAvatar, getDesk, getBrain } = lookups;

  const renderTile = (activity: AuditLog) => (
    <TileActivityLog
      avatar={getAvatar(activity.source_id, activity.source_type)}
      subject={getSubject(activity.source_id, activity.source_name, activity.source_type)}
      verb={activity.verb}
      targetType={activity.target_type}
      targetName={resolveTargetName(activity, getBrain)}
      brain={getBrain(activity.brain_id)}
      brainId={activity.brain_id}
      deskId={activity.desk_id}
      desk={getDesk(activity.desk_id)}
      timestamp={activity.created}
      targetId={activity.target_id}
      extra={activity.extra}
      hideAgentLink={hideAgentLink}
      hideTimestamp
      getBrain={getBrain}
    />
  );

  const renderTimestamp = (activity: AuditLog) =>
    activity.created ? (
      <TimestampTooltip timestamp={activity.created} side="left">
        {timeFromNowShort(activity.created)}
      </TimestampTooltip>
    ) : (
      '-'
    );

  // A single feed line: fixed timestamp gutter (left-aligned, muted) + the
  // activity sentence. Timestamps live in the gutter, never inline.
  const renderRow = (activity: AuditLog) => (
    <div className="flex" data-testid="activity-row">
      <div className="w-24 shrink-0 pt-1 pr-2 text-xs leading-6 text-muted-foreground">
        {renderTimestamp(activity)}
      </div>
      <div className="min-w-0 flex-1">{renderTile(activity)}</div>
    </div>
  );

  // Nested line: the field-change bullets — or the full sentence when there is no diff.
  const renderNestedRow = (activity: AuditLog) => (
    <div className="flex min-w-0 items-start gap-2 pt-4 pb-2 pl-6" data-testid="activity-row">
      <div className="min-w-0 flex-1">
        {hasAuditLogDiff(activity.extra) ? (
          <AuditLogFieldChanges extra={activity.extra} getBrain={getBrain} />
        ) : (
          renderTile(activity)
        )}
      </div>
      <div className="shrink-0 text-xs leading-6 text-muted-foreground">
        {renderTimestamp(activity)}
      </div>
    </div>
  );

  // A single activity with a JSON diff renders as a group of one, diff collapsed.
  const hasJsonDiff = hasAuditLogDiff(group.extra);

  if (!isGrouped && !hasJsonDiff) {
    return renderRow(group);
  }

  const activities = isGrouped ? group.nested : [group];
  // Strip the group-of-one header's extra so the diff only shows once revealed.
  const header = isGrouped ? group : { ...group, extra: {} };

  // Collapsed: elbow + toggle, nested rows not mounted. Expanded: toggle gone.
  return (
    <div data-testid="activity-group">
      {renderRow(header)}

      <div className="mt-1 flex">
        <span className="w-24 shrink-0" aria-hidden />
        {expanded ? (
          <div className="ml-2.5 flex min-w-0 flex-1 flex-col border-l border-muted-foreground/50">
            {activities.map((activity) => (
              <div key={activity.log_id}>{renderNestedRow(activity)}</div>
            ))}
          </div>
        ) : (
          <div className="ml-2.5 flex min-w-0 flex-1 items-stretch">
            <BranchConnector />
            <Button
              variant="ghost-neutral"
              size="sm"
              onClick={() => setExpanded(true)}
              className="ml-1 text-muted-foreground hover:text-foreground"
            >
              <ChevronDownIcon size={16} />
              {t('activityLogs.view_activities', { count: activities.length })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityGroupRow;
